
import React, { useState, forwardRef } from 'react';
import { FieldError } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import { FormField } from './FormField';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface PasswordFieldProps {
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  error?: FieldError;
  showStrength?: boolean;
  value?: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(({
  label,
  name,
  placeholder,
  required = false,
  autoComplete,
  error,
  showStrength = false,
  value,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  const getPasswordStrength = (password: string) => {
    if (password.length < 6) return { strength: 0, label: 'Muy débil', color: 'text-semantic-error' };
    if (password.length < 8) return { strength: 1, label: 'Débil', color: 'text-semantic-warning' };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score >= 4) return { strength: 3, label: 'Fuerte', color: 'text-semantic-success' };
    if (score >= 2) return { strength: 2, label: 'Media', color: 'text-yellow-500' };
    return { strength: 1, label: 'Débil', color: 'text-semantic-warning' };
  };

  // Get the password value for strength calculation
  const passwordValue = value || '';
  const passwordStrength = getPasswordStrength(passwordValue);

  return (
    <FormField
      label={label}
      name={name}
      required={required}
      error={error}
    >
      <div className="relative group">
        <Input
          id={name}
          name={name}
          type={showPassword ? 'text' : 'password'}
          autoComplete={autoComplete}
          required={required}
          className={cn(
            'pr-10 transition-all duration-200',
            'focus:ring-2 focus:ring-brand-primary focus:border-brand-primary focus:ring-offset-1',
            'hover:border-neutral-400',
            'group-hover:shadow-sm',
            error && 'border-semantic-error focus:ring-semantic-error'
          )}
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          ref={ref}
          value={value}
          {...props}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-400 hover:text-neutral-600 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-1 rounded-sm"
          onClick={() => setShowPassword(!showPassword)}
          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4 transition-transform duration-200" />
          ) : (
            <Eye className="h-4 w-4 transition-transform duration-200" />
          )}
        </button>
      </div>

      {showStrength && passwordValue && (
        <div className="mt-2 animate-fade-in">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-neutral-600">Seguridad:</span>
            <span className={cn(passwordStrength.color, 'transition-colors duration-200')}>
              {passwordStrength.label}
            </span>
          </div>
          <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
            <div
              className={cn(
                'h-2 rounded-full transition-all duration-500 ease-out',
                passwordStrength.strength === 0 && 'bg-semantic-error w-1/4',
                passwordStrength.strength === 1 && 'bg-semantic-warning w-2/4',
                passwordStrength.strength === 2 && 'bg-yellow-500 w-3/4',
                passwordStrength.strength === 3 && 'bg-semantic-success w-full'
              )}
            />
          </div>
        </div>
      )}
    </FormField>
  );
});

PasswordField.displayName = "PasswordField";
